"use client";

var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { GripVertical } from "lucide-react";
import { Reorder, useDragControls, useReducedMotion } from "motion/react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils.js";
var ReorderableContext = createContext(null);
function useReorderable() {
  var context = useContext(ReorderableContext);
  if (!context) {
    throw new Error("ReorderableItem must be rendered inside ReorderableList");
  }
  return context;
}
var KEYBOARD_COMMIT_DELAY_MS = 600;
export function ReorderableList(_a) {
  var _b;
  var ids = _a.ids,
    onReorder = _a.onReorder,
    onCommit = _a.onCommit,
    _c = _a.as,
    as = _c === void 0 ? "div" : _c,
    className = _a.className,
    _d = _a.disabled,
    disabled = _d === void 0 ? false : _d,
    children = _a.children;
  var _e = useState(""),
    announcement = _e[0],
    setAnnouncement = _e[1];
  var _f = useState(false),
    mounted = _f[0],
    setMounted = _f[1];
  useEffect(function () {
    return setMounted(true);
  }, []);
  var commitTimer = useRef(null);
  var pendingCommit = useRef(null);
  var onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  var instructionsId = "reorder-instructions-".concat((_b = ids[0]) !== null && _b !== void 0 ? _b : "empty");
  useEffect(function () {
    return function () {
      if (commitTimer.current) {
        clearTimeout(commitTimer.current);
      }
      if (pendingCommit.current) {
        onCommitRef.current(pendingCommit.current);
        pendingCommit.current = null;
      }
    };
  }, []);
  var commit = useCallback(function (next) {
    if (commitTimer.current) {
      clearTimeout(commitTimer.current);
    }
    pendingCommit.current = next;
    commitTimer.current = setTimeout(function () {
      pendingCommit.current = null;
      onCommit(next);
    }, KEYBOARD_COMMIT_DELAY_MS);
  }, [onCommit]);
  var effectivelyDisabled = disabled || ids.length < 2;
  var context = {
    ids: ids,
    onReorder: onReorder,
    commit: commit,
    disabled: effectivelyDisabled,
    instructionsId: instructionsId,
    announce: setAnnouncement
  };
  return _jsxs(_Fragment, {
    children: [_jsx(Reorder.Group, {
      axis: "y",
      as: as,
      values: ids,
      onReorder: onReorder,
      className: className,
      children: _jsx(ReorderableContext.Provider, {
        value: context,
        children: children
      })
    }), mounted && createPortal(_jsxs(_Fragment, {
      children: [_jsx("span", {
        id: instructionsId,
        className: "sr-only",
        children: "Press the Up and Down arrow keys to move this item. Changes save automatically."
      }), _jsx("span", {
        "aria-live": "polite",
        className: "sr-only",
        children: announcement
      })]
    }), document.body)]
  });
}
export function ReorderableItem(_a) {
  var id = _a.id,
    _b = _a.as,
    as = _b === void 0 ? "div" : _b,
    className = _a.className,
    itemLabel = _a.itemLabel,
    children = _a.children;
  var context = useReorderable();
  var controls = useDragControls();
  var reduceMotion = useReducedMotion();
  return _jsx(Reorder.Item, {
    value: id,
    as: as,
    layout: "position",
    dragListener: false,
    dragControls: controls,
    className: cn("relative", className),
    transition: reduceMotion ? {
      duration: 0
    } : undefined,
    onDragEnd: function () {
      return context.commit(context.ids);
    },
    children: children(_jsx(ReorderHandle, {
      id: id,
      itemLabel: itemLabel,
      controls: controls
    }))
  });
}
function ReorderHandle(_a) {
  var id = _a.id,
    itemLabel = _a.itemLabel,
    controls = _a.controls;
  var _b = useReorderable(),
    ids = _b.ids,
    onReorder = _b.onReorder,
    commit = _b.commit,
    disabled = _b.disabled,
    instructionsId = _b.instructionsId,
    announce = _b.announce;
  var index = ids.indexOf(id);
  var buttonRef = useRef(null);
  function move(delta) {
    var target = index + delta;
    if (disabled || index === -1 || target < 0 || target >= ids.length) {
      return;
    }
    var next = __spreadArray([], ids, true);
    next.splice.apply(next, __spreadArray([target, 0], next.splice(index, 1), false));
    onReorder(next);
    commit(next);
    requestAnimationFrame(function () {
      var handle = document.querySelector("[data-reorder-handle=\"".concat(CSS.escape(id), "\"]"));
      handle === null || handle === void 0 ? void 0 : handle.focus();
    });
    announce("".concat(itemLabel, " moved to position ").concat(target + 1, " of ").concat(ids.length, "."));
  }
  return _jsx("button", {
    ref: buttonRef,
    type: "button",
    "data-reorder-handle": id,
    "aria-disabled": disabled,
    "aria-label": "Reorder ".concat(itemLabel, ": position ").concat(index + 1, " of ").concat(ids.length),
    "aria-describedby": instructionsId,
    onPointerDown: function (event) {
      if (!disabled && event.button === 0) {
        controls.start(event);
      }
    },
    onKeyDown: function (event_0) {
      if (event_0.key === "ArrowUp") {
        event_0.preventDefault();
        move(-1);
      } else if (event_0.key === "ArrowDown") {
        event_0.preventDefault();
        move(1);
      }
    },
    className: cn("flex h-7 w-6 shrink-0 items-center justify-center rounded text-muted-foreground", "touch-none select-none outline-none", "focus-visible:ring-[3px] focus-visible:ring-ring/50", disabled ? "cursor-default opacity-30" : "cursor-grab hover:text-foreground active:cursor-grabbing"),
    children: _jsx(GripVertical, {
      className: "h-4 w-4",
      "aria-hidden": true
    })
  });
}